<script lang="ts">
	import Fingerprint from '$lib/components/Fingerprint.svelte';
	import { page } from '$app/stores';

	const site = $derived($page.data.site);
	const keyUrl = $derived(`${site.urls.home.replace(/\/$/, '')}${site.pgp.path}`);
</script>

<svelte:head>
	<title>{site.verify.pageTitle}</title>
	<meta name="description" content={site.verify.description} />
	<meta property="og:title" content={site.verify.pageTitle} />
	<meta property="og:description" content={site.verify.description} />
	<meta property="og:url" content="{site.urls.home}/verify" />
	<link rel="canonical" href="{site.urls.home}/verify" />
</svelte:head>

<main id="main" class="verify-page">
	<header class="guide-masthead">
		<div class="container guide-wrap">
			<nav class="guide-crumb" aria-label="Breadcrumb">
				<a href="/#verify">Back to home</a>
			</nav>
			<p class="eyebrow">{site.verify.eyebrow}</p>
			<h1>{site.verify.title}</h1>
			<p class="guide-lead">{site.verify.lead}</p>
			<div class="hero-actions">
				<a class="button button-primary" href={site.pgp.path}>Download public key</a>
				<a class="button button-secondary" href="#fingerprint">Jump to fingerprint</a>
			</div>
		</div>
	</header>

	<article class="container guide-wrap guide-body">
		<section class="guide-section" aria-labelledby="why-heading">
			<h2 id="why-heading">Why verification matters</h2>
			<p>
				OpenPGP (often called PGP) lets someone prove that a piece of text or a file was signed with
				a specific private key, and that the content has not changed since it was signed. A
				signature is not a guarantee of identity by itself. It only becomes useful after you have
				independently confirmed that the matching public key is the one published here.
			</p>
			<p>
				This site is a place to obtain that public key and the fingerprint printed with it.
				Verification itself is done in OpenPGP-compatible software on your computer or in your mail
				client. Nothing on this page can mark a message valid for you.
			</p>
		</section>

		<section class="guide-section" aria-labelledby="need-heading">
			<h2 id="need-heading">What you need</h2>
			<ul class="need-list">
				<li>
					<strong>The signed material</strong> — the email, message, or file exactly as it arrived, plus
					any separate signature that came with it.
				</li>
				<li>
					<strong>The public key</strong> — published on this site at
					<a href={site.pgp.path}>{keyUrl}</a>.
				</li>
				<li>
					<strong>The fingerprint</strong> — printed below. Compare it with the key you imported before
					treating that key as mine.
				</li>
				<li>
					<strong>OpenPGP software</strong> — a mail client or application that can import a public key
					and check a signature. Use the verify function in the software you already trust.
				</li>
			</ul>
		</section>

		<section id="fingerprint" class="guide-section" aria-labelledby="key-heading">
			<h2 id="key-heading">Obtain the key, then authenticate it</h2>
			<p>
				Download the ASCII-armored public key from this site, then import it into your OpenPGP
				software. After import, display the key’s fingerprint and compare it with the value
				published here. Every character must match.
			</p>
			<Fingerprint fingerprint={site.pgp.fingerprint} />
			<p>
				This domain also publishes the same key through a Web Key Directory under
				<code>/.well-known/openpgpkey/</code>. That is another copy hosted here — not a second
				opinion. A key retrieved automatically is still untrusted until the fingerprint matches.
			</p>
			<aside class="caution" role="note">
				<p>
					<strong>Do not trust a key only because you downloaded it.</strong> Anyone can publish a file
					that claims a name. The download from this site is a convenience. The check is the fingerprint.
					When you can, confirm the same fingerprint through a second channel you already trust.
				</p>
			</aside>
		</section>

		<section class="guide-section" aria-labelledby="steps-heading">
			<h2 id="steps-heading">How to verify</h2>
			<p>
				The exact clicks or commands depend on your software. The sequence does not. Follow your
				application’s own documentation for importing a key and checking a signature.
			</p>
			<ol class="guide-steps">
				<li>
					<h3>Keep the original</h3>
					<p>
						Do not retype or “clean up” the signed text. Save the email, message, or file as you
						received it. If a detached signature arrived beside a document, keep both files
						together.
					</p>
				</li>
				<li>
					<h3>Import the public key</h3>
					<p>
						Open the downloaded key file in your OpenPGP software, or import
						<a href={site.pgp.path}>{site.pgp.path}</a> from this site.
					</p>
				</li>
				<li>
					<h3>Compare the fingerprint</h3>
					<p>
						Display the imported key and check it against the published fingerprint above. If it
						differs, stop. Do not use that key.
					</p>
				</li>
				<li>
					<h3>Check the signature</h3>
					<p>
						Use your software’s verify action on the signed email, the signed message, or the file
						and its signature. Read the result using the meanings below — not a screenshot from
						someone else.
					</p>
				</li>
			</ol>
		</section>

		<section class="guide-section" aria-labelledby="cases-heading">
			<h2 id="cases-heading">Email, messages, and files</h2>
			<div class="case-list">
				<article class="case">
					<h3>Signed email</h3>
					<p>
						A signed message may carry an inline signature or a signature the mail client attaches.
						Open the message in software that understands OpenPGP, after the published key is
						imported and the fingerprint matches. If the mail client has no OpenPGP support, save
						the raw signed message and verify it in a separate OpenPGP application.
					</p>
				</article>
				<article class="case">
					<h3>Signed message</h3>
					<p>
						A standalone signed note is usually a cleartext signed block or a short text file plus a
						signature. Verify that file (and the signature file, if it is separate) with the same
						imported key. Do not copy the text into a new document first.
					</p>
				</article>
				<article class="case">
					<h3>Signed files or other material</h3>
					<p>
						If a document was signed, you should have the original file and the signature that
						belongs to it. Point your OpenPGP software at both. A valid result applies only to that
						exact file — a re-exported or converted copy will not match.
					</p>
				</article>
			</div>
		</section>

		<section class="guide-section" aria-labelledby="results-heading">
			<h2 id="results-heading">What the result means</h2>
			<ul class="result-list">
				<li class="result result-valid">
					<h3>Valid signature</h3>
					<p>
						The software reports that the signature matches this public key and that the content has
						not changed since it was signed. That is only as strong as your fingerprint check. If
						you skipped that check, a “valid” result only means some key you imported made the
						signature.
					</p>
				</li>
				<li class="result result-invalid">
					<h3>Invalid signature</h3>
					<p>
						The content does not match the signature, the signature is incomplete, or it does not
						belong to this key. Treat the material as untrustworthy. Do not assume a typing error —
						obtain the original again from the source.
					</p>
				</li>
				<li class="result result-unknown">
					<h3>Unknown or untrusted key</h3>
					<p>
						The signature may be mathematically fine, but the software does not yet treat this key
						as authenticated. That is the usual state after a first import. Compare the fingerprint
						published on this page before you assign trust in your software. Until then, do not
						treat the signer as verified.
					</p>
				</li>
			</ul>
		</section>

		<section class="guide-section" aria-labelledby="warn-heading">
			<h2 id="warn-heading">Security notes</h2>
			<ul class="need-list">
				<li>
					A public key file is not an identity. The fingerprint comparison is the identity check.
				</li>
				<li>
					Keys copied from search engines, chat, or forwarded attachments are unauthenticated until
					they match the fingerprint published here.
				</li>
				<li>
					Web Key Directory on this domain is another distribution path for the same key. It does
					not replace a fingerprint check.
				</li>
				<li>
					This website cannot see the message you are checking and cannot produce a valid or invalid
					result for you.
				</li>
			</ul>
		</section>
	</article>
</main>
